// Attachments.jsx
import React, { useState, useRef } from "react";
import { Paperclip, X } from "lucide-react";

const Attachments = ({ isOpen, onClose, onApply, attachedFiles = [] }) => {
    const [files, setFiles] = useState(attachedFiles);
    const inputRef = useRef(null);

    const handleSelectFiles = (e) => {
        const newFiles = Array.from(e.target.files).map(file => ({
            file,
            name: file.name,
            size: file.size,
            progress: 0,
            id: Math.random().toString(36),
            showProgress: true // UPDATED
        }));
        setFiles(prev => [...prev, ...newFiles]);
        simulateUpload(newFiles);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const dropped = Array.from(e.dataTransfer.files).map(file => ({
            file,
            name: file.name,
            size: file.size,
            progress: 0,
            id: Math.random().toString(36),
            showProgress: true // UPDATED
        }));
        setFiles(prev => [...prev, ...dropped]);
        simulateUpload(dropped);
    };

    const simulateUpload = (items) => {
        items.forEach(item => {
            let progress = 0;
            const timer = setInterval(() => {
                progress += 10;

                setFiles(prev =>
                    prev.map(f =>
                        f.id === item.id
                            ? { ...f, progress: Math.min(progress, 100) }
                            : f
                    )
                );

                if (progress >= 100) {
                    clearInterval(timer);

                    // HIDE BAR AFTER 600MS
                    setTimeout(() => {
                        setFiles(prev =>
                            prev.map(f =>
                                f.id === item.id
                                    ? { ...f, showProgress: false } // UPDATED
                                    : f
                            )
                        );
                    }, 600);
                }
            }, 80);
        });
    };

    const removeFile = (id) => {
        setFiles(prev => prev.filter(f => f.id !== id));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div
                onClick={e => e.stopPropagation()}
                className="bg-white rounded-lg shadow-xl p-3 w-80 max-w-full">

                <h2 className="text-lg font-semibold tracking-wide mb-3">Attachments</h2>

                {/* Drag & Drop */}
                <div
                    onDrop={handleDrop}
                    onDragOver={e => e.preventDefault()}
                    onClick={() => inputRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition"
                >
                    <Paperclip size={32} className="mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-600">Drop files here or click to browse</p>
                    <input ref={inputRef} type="file" multiple className="hidden" onChange={handleSelectFiles} />
                </div>

                {/* File List */}
                <div className="mt-4 max-h-64 overflow-y-auto space-y-2">
                    {files.length === 0 ? (
                        <p className="text-center text-gray-500 text-sm py-4">No files attached yet</p>
                    ) : (
                        files.map(item => (
                            <div
                                key={item.id}
                                className="flex items-center justify-between bg-gray-50 rounded-lg p-3"
                            >
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{item.name}</p>
                                    <p className="text-xs text-gray-500">
                                        {(item.size / 1024 / 1024).toFixed(2)} MB
                                    </p>

                                    {/* PROGRESS BAR */}
                                    {item.showProgress && (
                                        <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                                            <div
                                                className={`
                                                    h-1.5 rounded-full transition-all duration-300
                                                    ${item.progress < 100 ? "bg-blue-600" : "bg-green-600"} 
                                                `}
                                                style={{ width: `${item.progress}%` }}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Remove only when done */}
                                {item.progress === 100 && (
                                    <button
                                        onClick={() => removeFile(item.id)}
                                        className="ml-3 text-red-500 hover:text-red-700"
                                    >
                                        <X size={18} />
                                    </button>
                                )}
                            </div>
                        ))
                    )}
                </div>

                {/* Buttons */}
                <div className="flex justify-end gap-3 mt-6">
                    <button
                        onClick={onClose}
                        className="text-sm px-3 py-1 bg-gray-200 rounded-lg hover:bg-gray-300 transition"
                    >
                        Cancel
                    </button>

                    <button
                        onClick={() => {
                            onApply(files.filter(f => f.progress === 100));
                            onClose();
                        }}
                        className="text-sm px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                    >
                        Apply ({files.filter(f => f.progress === 100).length})
                    </button>
                </div>

            </div>
        </div>
    );
};

export default Attachments;
